/**
 * Circular Impact Loop — 360° Interactive Orbital Hub (Circular Ring Progress)
 */
class CircularImpactLoop extends HTMLElement {
  constructor() {
    super();
    this.nodes = this.querySelectorAll('.js-orbit-node');
    this.mobilePills = this.querySelectorAll('.js-mobile-pill');
    this.panes = this.querySelectorAll('.js-card-pane');
    this.progressCircles = this.querySelectorAll('.js-progress-circle');
    this.layout = this.querySelector('.circular-impact__layout');

    this.currentIndex = 0;
    this.totalNodes = this.panes.length || this.nodes.length;
    this.autoCycleDuration = 6000; // 6 seconds per pillar
    this.circumference = 150.8;
    this.animationFrame = null;
    this.lastTimestamp = null;
    this.progressElapsed = 0;
    this.isPaused = false;
    this.touchStartX = 0;
  }

  connectedCallback() {
    if (!this.totalNodes) return;
    this.initEvents();
    this.setActive(0, false);
    this.startAutoCycle();
  }

  disconnectedCallback() {
    this.stopAutoCycle();
  }

  initEvents() {
    this.nodes.forEach((node, idx) => {
      node.addEventListener('click', (e) => {
        e.preventDefault();
        this.setActive(idx, true);
      });

      node.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.setActive(idx, true);
        }
      });
    });

    this.mobilePills.forEach((pill, idx) => {
      pill.addEventListener('click', (e) => {
        e.preventDefault();
        this.setActive(idx, true);
      });
    });

    // Pause auto-rotation on hover
    if (this.layout) {
      this.layout.addEventListener('mouseenter', () => this.pauseAutoCycle());
      this.layout.addEventListener('mouseleave', () => this.resumeAutoCycle());
      this.layout.addEventListener('focusin', () => this.pauseAutoCycle());
      this.layout.addEventListener('focusout', () => this.resumeAutoCycle());

      // Touch swipe support for spotlight card
      this.layout.addEventListener('touchstart', (e) => {
        this.touchStartX = e.touches[0].clientX;
        this.pauseAutoCycle();
      }, { passive: true });

      this.layout.addEventListener('touchend', (e) => {
        const touchEndX = e.changedTouches[0].clientX;
        const diff = touchEndX - this.touchStartX;
        if (Math.abs(diff) > 40) {
          if (diff < 0) {
            this.next(true);
          } else {
            this.prev(true);
          }
        }
        this.resumeAutoCycle();
      }, { passive: true });
    }
  }

  setActive(index, userInteracted = false) {
    this.currentIndex = (index + this.totalNodes) % this.totalNodes;

    // Update Orbital Nodes
    this.nodes.forEach((node, i) => {
      const isActive = i === this.currentIndex;
      node.classList.toggle('is-active', isActive);
      node.setAttribute('aria-selected', isActive ? 'true' : 'false');
      node.setAttribute('tabindex', isActive ? '0' : '-1');
    });

    // Update Mobile Pills (container-only horizontal scroll, never moves the page)
    const mobileNav = this.querySelector('.circular-impact__mobile-nav');
    this.mobilePills.forEach((pill, i) => {
      const isActive = i === this.currentIndex;
      pill.classList.toggle('is-active', isActive);
      pill.setAttribute('aria-selected', isActive ? 'true' : 'false');
      if (isActive && mobileNav) {
        const scrollTarget = pill.offsetLeft - (mobileNav.clientWidth / 2) + (pill.offsetWidth / 2);
        mobileNav.scrollTo({ left: scrollTarget, behavior: 'smooth' });
      }
    });

    // Update Spotlight Panes (Smooth Cross-Dissolve)
    this.panes.forEach((pane, i) => {
      const isActive = i === this.currentIndex;
      pane.classList.toggle('is-active', isActive);
    });

    this.resetProgress();
  }

  next(userInteracted = false) {
    this.setActive(this.currentIndex + 1, userInteracted);
  }

  prev(userInteracted = false) {
    this.setActive(this.currentIndex - 1, userInteracted);
  }

  startAutoCycle() {
    this.stopAutoCycle();
    this.progressElapsed = 0;
    this.lastTimestamp = performance.now();

    const loop = (currentTimestamp) => {
      if (!this.lastTimestamp) this.lastTimestamp = currentTimestamp;
      const deltaTime = currentTimestamp - this.lastTimestamp;
      this.lastTimestamp = currentTimestamp;

      if (!this.isPaused) {
        this.progressElapsed += deltaTime;
        const progressRatio = Math.min(this.progressElapsed / this.autoCycleDuration, 1);
        const offset = this.circumference * (1 - progressRatio);

        if (this.progressCircles.length) {
          this.progressCircles.forEach(circle => {
            circle.style.strokeDashoffset = `${offset.toFixed(2)}px`;
          });
        }

        if (this.progressElapsed >= this.autoCycleDuration) {
          this.progressElapsed = 0;
          this.next(false);
        }
      }

      this.animationFrame = requestAnimationFrame(loop);
    };

    this.animationFrame = requestAnimationFrame(loop);
  }

  resetProgress() {
    this.progressElapsed = 0;
    this.lastTimestamp = performance.now();
    if (this.progressCircles.length) {
      this.progressCircles.forEach(circle => {
        circle.style.strokeDashoffset = `${this.circumference}px`;
      });
    }
  }

  pauseAutoCycle() {
    this.isPaused = true;
  }

  resumeAutoCycle() {
    this.isPaused = false;
    this.lastTimestamp = performance.now();
  }

  stopAutoCycle() {
    if (this.animationFrame) {
      cancelAnimationFrame(this.animationFrame);
      this.animationFrame = null;
    }
    this.lastTimestamp = null;
  }
}

if (!customElements.get('circular-impact-loop')) {
  customElements.define('circular-impact-loop', CircularImpactLoop);
}
